<template>
  <div class="access-denied-container">
    <div class="card-surface">
      <!-- Status Badge -->
      <div class="badge-wrapper">
        <span v-if="isProductNotEnabled" class="status-badge badge-warning">
          <i class="fa-solid fa-triangle-exclamation"></i>
          Product Not Enabled
        </span>
        <span v-else class="status-badge badge-danger">
          <i class="fa-solid fa-ban"></i>
          403 Forbidden — Access Denied
        </span>
      </div>

      <!-- Icon Graphic -->
      <div class="icon-container" :class="{ 'warning-icon': isProductNotEnabled, 'danger-icon': !isProductNotEnabled }">
        <i v-if="isProductNotEnabled" class="fa-solid fa-building-circle-exclamation"></i>
        <i v-else class="fa-solid fa-lock"></i>
      </div>

      <!-- Mode 1: Product Not Enabled (Scenario B - Org Admin) -->
      <template v-if="isProductNotEnabled">
        <h2 class="title">Spark Is Not Enabled for This Organisation</h2>
        <p class="description">
          Your organisation does not currently have the Spark platform enabled.
          To activate Spark for your tenant, please contact your account manager or our enterprise sales team.
        </p>
        <div class="info-callout">
          <i class="fa-solid fa-circle-info callout-icon"></i>
          <span>As an Organisation Administrator, you can return to Organisation Settings to manage products and memberships.</span>
        </div>
      </template>

      <!-- Mode 2: Forbidden (Scenario A - General User) -->
      <template v-else>
        <h2 class="title">Access Denied</h2>
        <p class="description">
          Your user account does not have permission to access the Spark platform.
          If you believe you should have access, please contact your Organisation Administrator to request entitlement.
        </p>
        <div class="info-callout">
          <i class="fa-solid fa-circle-info callout-icon"></i>
          <span>Only authorized administrators, consultants, and designated users can access Spark workspaces.</span>
        </div>
      </template>

      <!-- Action Buttons -->
      <div class="actions-wrapper">
        <!-- Scenario B: Org Admin with valid org ID can go directly to Org Settings -->
        <a
          v-if="isProductNotEnabled && orgSettingsUrl"
          :href="orgSettingsUrl"
          class="btn-primary"
          @click="handleClearSession"
        >
          <i class="fa-solid fa-gear me-2"></i>
          Go to Organisation Settings
        </a>

        <!-- Primary CTA: Return to Auth Portal (clean redirect without redirect_url) -->
        <a
          :href="authPortalUrl"
          :class="isProductNotEnabled && orgSettingsUrl ? 'btn-secondary' : 'btn-primary'"
          @click="handleClearSession"
        >
          <i class="fa-solid fa-arrow-left me-2"></i>
          Return to Auth Portal
        </a>

        <!-- Secondary CTA: Switch Account / Sign Out -->
        <button
          type="button"
          class="btn-secondary"
          @click="handleSignOut"
        >
          <i class="fa-solid fa-right-from-bracket me-2"></i>
          Switch Account / Sign Out
        </button>
      </div>
    </div>

    <!-- Footer Branding -->
    <a href="https://vertexcybersecurity.com.au" rel="noopener noreferrer" target="_blank" class="logo-link">
      <img :src="logo" alt="Vertex Cyber Security Logo" class="logo" />
    </a>
  </div>
</template>

<script>
import vertexLogo from '@/assets/img/vertex_logo.png';
import config from '@/plugins/config';

export default {
  name: 'AccessDenied',
  data() {
    return {
      logo: vertexLogo,
    };
  },
  computed: {
    sanitizedReason() {
      const rawReason = this.$route.query.reason;
      if (rawReason === 'product_not_enabled') {
        return 'product_not_enabled';
      }
      return 'forbidden';
    },
    isProductNotEnabled() {
      return this.sanitizedReason === 'product_not_enabled';
    },
    sanitizedOrgId() {
      const raw = this.$route.query.org_id;
      if (typeof raw === 'string' && /^[a-zA-Z0-9-]+$/.test(raw)) {
        return raw;
      }
      if (typeof raw === 'number' && Number.isInteger(raw)) {
        return String(raw);
      }
      return '';
    },
    authBaseUrl() {
      const url = config.getAuthBaseUrl() || '';
      return url.replace(/\/+$/, '');
    },
    orgSettingsUrl() {
      if (!this.authBaseUrl) return '';
      return `${this.authBaseUrl}/user-settings/`;
    },
    authPortalUrl() {
      if (!this.authBaseUrl) return '/';
      return `${this.authBaseUrl}/user-settings/`;
    },
    signOutUrl() {
      if (!this.authBaseUrl) return '/';
      return `${this.authBaseUrl}/login/?logout=true`;
    },
  },
  mounted() {
    // Immediately clear local authentication state to halt any recursive redirects
    this.handleClearSession();
    if (this.authBaseUrl) {
      window.location.href = `${this.authBaseUrl}/user-settings/`;
    }
  },
  methods: {
    handleClearSession() {
      try {
        if (this.$store && typeof this.$store.dispatch === 'function') {
          this.$store.dispatch('auth/clearLocalSession');
        }
      } catch (e) {
        console.warn('[AccessDenied] Could not clear local session:', e);
      }
    },
    handleSignOut() {
      this.handleClearSession();
      window.location.href = this.signOutUrl;
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700&display=swap');

.access-denied-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 30px 20px;
  background-color: #F4FBFF;
  background-image: url("~@/assets/img/BG_Mountain.jpg");
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  font-family: 'Roboto', sans-serif;
  text-align: center;
}

.card-surface {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 580px;
  background-color: rgba(255, 255, 255, 0.95);
  padding: 44px 40px;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  margin-bottom: 28px;
  animation: card-appear 0.4s ease-out forwards;
}

.badge-wrapper {
  margin-bottom: 20px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.badge-warning {
  background-color: #FFDEC3;
  color: #F3994D;
}

.badge-danger {
  background-color: #FFB5B9;
  color: #EB1C24;
}

.icon-container {
  width: 76px;
  height: 76px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  margin-bottom: 20px;
  transition: transform 0.2s ease;
}

.warning-icon {
  background-color: #FFF2E7;
  color: #F3994D;
}

.danger-icon {
  background-color: #FFEAEB;
  color: #EB1C24;
}

.title {
  font-size: 1.6rem;
  font-weight: 600;
  color: #435666;
  margin: 0 0 14px 0;
  line-height: 1.3;
}

.description {
  font-size: 0.975rem;
  color: #5C768B;
  line-height: 1.6;
  margin: 0 0 20px 0;
  max-width: 480px;
}

.info-callout {
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  padding: 12px 16px;
  font-size: 0.875rem;
  color: #5C768B;
  text-align: left;
  margin-bottom: 28px;
  width: 100%;
}

.callout-icon {
  color: #00A0C1;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.actions-wrapper {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

@media (min-width: 480px) {
  .actions-wrapper {
    flex-direction: row;
    justify-content: center;
    flex-wrap: wrap;
  }
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #00A0C1;
  color: #FFFFFF;
  border: 1px solid #00A0C1;
  font-weight: 500;
  font-size: 0.95rem;
  padding: 10px 22px;
  border-radius: 8px;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.btn-primary:hover {
  background-color: #15B7D9;
  border-color: #15B7D9;
  color: #FFFFFF;
}

.btn-secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #FFFFFF;
  color: #435666;
  border: 1px solid #D9D9D9;
  font-weight: 500;
  font-size: 0.95rem;
  padding: 10px 22px;
  border-radius: 8px;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.btn-secondary:hover {
  background-color: #F8FAFC;
  border-color: #A3C0D1;
  color: #435666;
}

.logo-link {
  text-decoration: none;
  display: inline-block;
  opacity: 0.85;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.logo-link:hover {
  opacity: 1;
  transform: scale(1.02);
}

.logo {
  max-width: 220px;
  height: auto;
}

@keyframes card-appear {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
